import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  type ReactNode,
} from 'react';

/** A single shortcut registration entry. */
export interface ShortcutEntry {
  /** Unique identifier for this shortcut */
  id: string;
  /** Key combination string (e.g. "S", "Ctrl+B", "Delete") */
  keys: string;
  /** i18n label key for display */
  label: string;
  /** Category for grouping in overlay */
  category: 'tool' | 'canvas' | 'sidebar' | 'global';
  /** Action to perform when shortcut is triggered */
  action: () => void;
  /** Optional guard — shortcut only fires if enabled() returns true */
  enabled?: () => boolean;
  /** Context condition (e.g. 'tool:draw') — for conflict detection */
  when?: string;
  /** Hold behavior: 'temporary-switch' fires action on keydown, revert on keyup */
  holdBehavior?: 'temporary-switch' | 'none';
  /** Called on keyup when holdBehavior is 'temporary-switch'. Use to revert tool state. */
  revertAction?: () => void;
}

/** Public API for the shortcut registry. */
export interface ShortcutRegistry {
  /** Register a shortcut entry. Returns an unregister function. */
  register(entry: ShortcutEntry): () => void;
  /** Get all registered shortcut entries. */
  getAll(): ShortcutEntry[];
  /** Get entries filtered by category. */
  getByCategory(category: ShortcutEntry['category']): ShortcutEntry[];
  /** Programmatically trigger a shortcut by keys string. Returns true if matched. */
  trigger(keys: string): boolean;
  /** Set overlay open state. When open, hold-to-switch reverts are deferred until close. */
  setOverlayOpen: (open: boolean) => void;
}

const ShortcutContext = createContext<ShortcutRegistry | null>(null);

// ---------------------------------------------------------------------------
// Key normalization: pre-parse key strings into a canonical form so that
// handleKeyDown only needs a single Map.get() instead of iterating all entries.
// ---------------------------------------------------------------------------

interface ParsedKey {
  ctrl: boolean;
  shift: boolean;
  alt: boolean;
  key: string; // normalized target key (e.g. "keys", "space", "delete", "1", "[")
}

/** Parse a shortcut definition string (e.g. "Ctrl+Shift+Z") into a ParsedKey. */
function parseKeyStr(keyStr: string): ParsedKey {
  const parts = keyStr.split('+');
  let ctrl = false;
  let shift = false;
  let alt = false;
  let targetKey = '';

  for (const part of parts) {
    const lower = part.trim().toLowerCase();
    if (lower === 'ctrl' || lower === 'meta' || lower === 'cmd') {
      ctrl = true;
    } else if (lower === 'shift') {
      shift = true;
    } else if (lower === 'alt') {
      alt = true;
    } else {
      targetKey = part.trim();
    }
  }

  // Normalize the target key to the same form we'll derive from events
  const tl = targetKey.toLowerCase();
  let normalizedKey: string;
  if (tl === 'space') normalizedKey = 'space';
  else if (tl === 'delete') normalizedKey = 'delete';
  else if (tl === 'backspace') normalizedKey = 'backspace';
  else if (tl === 'escape' || tl === 'esc') normalizedKey = 'escape';
  else if (/^[a-z]$/.test(tl)) normalizedKey = `key${tl}`;
  else normalizedKey = tl;

  return { ctrl, shift, alt, key: normalizedKey };
}

/** Build a canonical index key from parsed modifiers + key. */
function canonicalKey(p: ParsedKey): string {
  let prefix = '';
  if (p.alt) prefix += 'a+';
  if (p.ctrl) prefix += 'c+';
  if (p.shift) prefix += 's+';
  return prefix + p.key;
}

/** Normalize a key definition string to the canonical index key. */
function normalizeKeyStr(keyStr: string): string {
  return canonicalKey(parseKeyStr(keyStr));
}

/** Derive the canonical index key from a KeyboardEvent. */
function normalizeEvent(e: KeyboardEvent): string {
  const ctrlHeld = e.ctrlKey || e.metaKey;
  let prefix = '';
  if (e.altKey) prefix += 'a+';
  if (ctrlHeld) prefix += 'c+';
  if (e.shiftKey) prefix += 's+';

  const ek = e.key.toLowerCase();
  let key: string;
  if (ek === ' ') key = 'space';
  else if (ek === 'delete') key = 'delete';
  else if (ek === 'backspace') key = 'backspace';
  else if (ek === 'escape') key = 'escape';
  else if (e.code.startsWith('Key')) key = e.code.toLowerCase(); // e.g. "keys", "keya"
  else key = ek;

  return prefix + key;
}

/** Check if the active element is an input/textarea/select/contentEditable */
function isInputFocused(): boolean {
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (el.isContentEditable) return true;
  return false;
}

/** Provider for centralized keyboard shortcuts. */
export function ShortcutProvider({ children }: { children: ReactNode }) {
  const entriesRef = useRef(new Map<string, ShortcutEntry>());
  // O(1) key lookup index: canonical key string → array of entries
  const keyIndexRef = useRef(new Map<string, ShortcutEntry[]>());

  // Track held keys for hold-to-switch
  const holdActiveRef = useRef<Map<string, ShortcutEntry>>(new Map());

  // Overlay guard: defer reverts when an overlay is open
  const overlayOpenRef = useRef(false);
  const deferredRevertsRef = useRef(new Map<string, ShortcutEntry>());

  const register = useCallback((entry: ShortcutEntry): (() => void) => {
    // Conflict detection (dev mode)
    if (import.meta.env.DEV) {
      for (const existing of entriesRef.current.values()) {
        if (
          existing.keys === entry.keys &&
          existing.when === entry.when &&
          existing.id !== entry.id
        ) {
          // eslint-disable-next-line no-console
          console.warn(
            `[ShortcutRegistry] Conflict: "${entry.id}" and "${existing.id}" both register keys="${entry.keys}" when="${entry.when ?? 'any'}"`,
          );
        }
      }
    }
    entriesRef.current.set(entry.id, entry);

    // Add to key index
    const nk = normalizeKeyStr(entry.keys);
    const arr = keyIndexRef.current.get(nk);
    if (arr) {
      arr.push(entry);
    } else {
      keyIndexRef.current.set(nk, [entry]);
    }

    return () => {
      entriesRef.current.delete(entry.id);
      holdActiveRef.current.delete(entry.id);
      // Remove from key index
      const bucket = keyIndexRef.current.get(nk);
      if (bucket) {
        const idx = bucket.indexOf(entry);
        if (idx !== -1) bucket.splice(idx, 1);
        if (bucket.length === 0) keyIndexRef.current.delete(nk);
      }
    };
  }, []);

  const getAll = useCallback((): ShortcutEntry[] => {
    return Array.from(entriesRef.current.values());
  }, []);

  const getByCategory = useCallback((category: ShortcutEntry['category']): ShortcutEntry[] => {
    return Array.from(entriesRef.current.values()).filter((e) => e.category === category);
  }, []);

  const setOverlayOpen = useCallback((open: boolean) => {
    overlayOpenRef.current = open;
    if (!open) {
      // Execute all deferred reverts
      for (const entry of deferredRevertsRef.current.values()) {
        entry.revertAction?.();
      }
      deferredRevertsRef.current.clear();
    }
  }, []);

  const trigger = useCallback((keys: string): boolean => {
    const nk = normalizeKeyStr(keys);
    const candidates = keyIndexRef.current.get(nk);
    if (!candidates) return false;
    for (const entry of candidates) {
      if (entry.enabled && !entry.enabled()) continue;
      entry.action();
      return true;
    }
    return false;
  }, []);

  // Set up global keydown/keyup handlers
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Input guard: skip when focused on input (except Escape)
      if (isInputFocused() && e.key !== 'Escape') return;

      const nk = normalizeEvent(e);
      const candidates = keyIndexRef.current.get(nk);
      if (!candidates) return;

      for (const entry of candidates) {
        // Check enabled guard
        if (entry.enabled && !entry.enabled()) continue;

        e.preventDefault();

        if (entry.holdBehavior === 'temporary-switch') {
          // Track this for keyup revert
          holdActiveRef.current.set(entry.id, entry);
        }

        entry.action();
        return;
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      // Check if any hold-to-switch entries should revert
      for (const [id, entry] of holdActiveRef.current.entries()) {
        const nk = normalizeEvent(e);
        const entryNk = normalizeKeyStr(entry.keys);
        if (nk === entryNk) {
          holdActiveRef.current.delete(id);
          // Overlay guard: defer revert if overlay is open
          if (overlayOpenRef.current) {
            deferredRevertsRef.current.set(id, entry);
          } else if (entry.revertAction) {
            entry.revertAction();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  const registry = useMemo<ShortcutRegistry>(() => ({
    register,
    getAll,
    getByCategory,
    trigger,
    setOverlayOpen,
  }), [register, getAll, getByCategory, trigger, setOverlayOpen]);

  return (
    <ShortcutContext.Provider value={registry}>{children}</ShortcutContext.Provider>
  );
}

/**
 * Access ShortcutRegistry. Must be used within a ShortcutProvider.
 * @throws Error if used outside ShortcutProvider.
 */
export function useShortcutContext(): ShortcutRegistry {
  const ctx = useContext(ShortcutContext);
  if (!ctx) throw new Error('useShortcutContext must be used within ShortcutProvider');
  return ctx;
}
